// src/pages/app/ReferrerPortalPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getReferralsMe,
  getReferralsMeStats,
  monthKeyForDateUTC,
  ReferralsMeResponse,
  ReferralsMeStatsResponse
} from "../../api/referrals";

const formatCurrency = (amountCents: number, currency: string, locale: string) =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2
  }).format(amountCents / 100);

function shiftMonthKey(monthKey: string, delta: number): string {
  const [y, m] = monthKey.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return monthKeyForDateUTC(d);
}

const ReferrerPortalPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = useMemo(() => (i18n.resolvedLanguage?.startsWith("it") ? "it-IT" : "en-GB"), [
    i18n.resolvedLanguage
  ]);

  const [monthKey, setMonthKey] = useState<string>(() => monthKeyForDateUTC(new Date()));

  const [me, setMe] = useState<ReferralsMeResponse | null>(null);
  const [stats, setStats] = useState<ReferralsMeStatsResponse | null>(null);

  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const primaryCode = useMemo(() => {
    const active = me?.codes?.find((c) => c.isActive)?.code;
    return active || me?.codes?.[0]?.code || null;
  }, [me]);

  const referralLink = useMemo(() => {
    if (!primaryCode) return null;
    return `${window.location.origin}/?ref=${encodeURIComponent(primaryCode)}`;
  }, [primaryCode]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [meRes, statsRes] = await Promise.all([getReferralsMe(), getReferralsMeStats(monthKey)]);
        if (!mounted) return;
        setMe(meRes);
        setStats(statsRes);
      } catch (err: any) {
        if (!mounted) return;
        setError(err?.message || "Failed to load referrer data.");
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []); // initial

  useEffect(() => {
    if (!me) return;
    let mounted = true;

    (async () => {
      setLoadingStats(true);
      setError(null);
      try {
        const statsRes = await getReferralsMeStats(monthKey);
        if (!mounted) return;
        setStats(statsRes);
      } catch (err: any) {
        if (!mounted) return;
        setError(err?.message || "Failed to load stats.");
      } finally {
        if (!mounted) return;
        setLoadingStats(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [monthKey, me]);

  const handleCopy = async () => {
    if (!referralLink) return;
    await navigator.clipboard.writeText(referralLink);
  };

  return (
    <div className="page-root">
      <div className="page-header">
        <div>
          <h1>{t("referrer.title", "Referrer")}</h1>
          <p className="muted">{t("referrer.subtitle", "Your codes, conversions and payouts.")}</p>
        </div>

        <div className="page-header-actions">
          <button className="btn-secondary" type="button" onClick={() => setMonthKey(shiftMonthKey(monthKey, -1))}>
            ← {t("common.prev", "Prev")}
          </button>
          <div className="month-pill">{monthKey}</div>
          <button className="btn-secondary" type="button" onClick={() => setMonthKey(shiftMonthKey(monthKey, 1))}>
            {t("common.next", "Next")} →
          </button>
        </div>
      </div>

      {loading && <div className="card"><p>{t("common.loading", "Loading...")}</p></div>}

      {!loading && error && (
        <div className="card card-error">
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && me && (
        <>
          <div className="card">
            <h2 className="card-title">{t("referrer.yourCodes", "Your referral codes")}</h2>

            {me.codes.length === 0 ? (
              <p className="muted">{t("referrer.noCodes", "No codes yet. Ask an admin to create one.")}</p>
            ) : (
              <div className="ref-codes">
                {me.codes.map((c) => (
                  <span key={c.code} className={`badge ${c.isActive ? "badge-ok" : ""}`}>
                    {c.code} {c.isActive ? "• active" : ""}
                  </span>
                ))}
              </div>
            )}

            {referralLink && (
              <>
                <div className="ref-link-row">
                  <input className="ref-link-input" type="text" readOnly value={referralLink} />
                  <button className="btn-secondary" type="button" onClick={handleCopy}>
                    {t("common.copy", "Copy")}
                  </button>
                </div>
                <p className="muted" style={{ marginTop: "0.5rem" }}>
                  {t("referrer.linkHint", "Share this link. A cookie is set on click and applied at checkout.")}
                </p>
              </>
            )}
          </div>

          <div className="card" style={{ marginTop: "1rem" }}>
            <div className="card-header">
              <h2>{t("referrer.statsTitle", "Month stats")}</h2>
              <p className="card-subtitle">{loadingStats ? t("common.loading", "Loading...") : t("referrer.statsSub", "UTC month summary.")}</p>
            </div>

            {stats && (
              <>
                <div className="ref-stats-grid">
                  <div className="dashboard-kpi-card">
                    <div className="dashboard-kpi-label">{t("referrer.activeClients", "Active clients")}</div>
                    <div className="dashboard-kpi-value">{stats.activeAttributions}</div>
                  </div>

                  <div className="dashboard-kpi-card">
                    <div className="dashboard-kpi-label">{t("referrer.conversions", "Conversions")}</div>
                    <div className="dashboard-kpi-value">{stats.conversionsThisMonth}</div>
                  </div>
                </div>

                <hr className="section-separator" />

                <h3 style={{ margin: 0 }}>{t("referrer.totalsByCurrency", "Totals by currency")}</h3>

                {stats.totalsByCurrency.length === 0 ? (
                  <p className="muted" style={{ marginTop: "0.5rem" }}>
                    {t("referrer.noTotals", "No commissions recorded for this month yet.")}
                  </p>
                ) : (
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>{t("common.currency", "Currency")}</th>
                          <th>{t("referrer.revenue", "Revenue")}</th>
                          <th>{t("referrer.commission", "Commission")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.totalsByCurrency.map((r) => (
                          <tr key={r.currency}>
                            <td>{r.currency.toUpperCase()}</td>
                            <td>{formatCurrency(r.revenueCents, r.currency, locale)}</td>
                            <td>{formatCurrency(r.commissionCents, r.currency, locale)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <h3 style={{ marginTop: "1.2rem" }}>{t("referrer.payoutPeriods", "Payout periods")}</h3>
                {stats.payoutPeriods.length === 0 ? (
                  <p className="muted">{t("referrer.noPayouts", "No payout periods for this month.")}</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>{t("common.currency", "Currency")}</th>
                          <th>{t("common.amount", "Amount")}</th>
                          <th>{t("common.status", "Status")}</th>
                          <th>{t("common.paidAt", "Paid at")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.payoutPeriods.map((p, idx) => (
                          <tr key={`${p.currency}-${idx}`}>
                            <td>{p.currency.toUpperCase()}</td>
                            <td>{formatCurrency(p.amountCents, p.currency, locale)}</td>
                            <td>{p.status}</td>
                            <td>{p.paidAt ? new Date(p.paidAt).toLocaleString(locale) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ReferrerPortalPage;
